import React, { useEffect } from "react";
import {
  BackButton,
  BackToTopButton,
  FloatingMenuPill,
  IconName,
  TravelSalesEventBanner,
  UserPreferencesAppliedBanner,
} from "halifax";
import {
  Box,
  Divider,
  Link,
  Typography,
  useScrollTrigger,
} from "@material-ui/core";
import clsx from "clsx";

import { AvailabilityMap } from "../AvailabilityMap";
import { AvailabilityFilter } from "../AvailabilityFilter";
import { MobileLocationSelection } from "../MobileLocationSelection";
import { AvailabilityNoResults } from "../AvailabilityNoResults";
import { MobileAvailabilityMapRollingGallery } from "../MobileAvailabilityMapRollingGallery";
import { MobileHotelAvailabilityConnectorProps } from "./container";
import "./styles.scss";
import { AvailabilityList } from "../AvailabilityList";
import { RewardsAccountSelection } from "../../../rewards/components";
import { HotelAvailabilityCallState } from "../../reducer/state";
import { TravelWalletDrawer } from "../../../travel-wallet/components";
import { COUNT_HEADING_TEXT } from "../AvailabilityList/textConstants";
import {
  AVAILABLE,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  TRAVEL_SALE_ACTIVE,
  TRAVEL_SALE_VARIANTS,
  TRAVEL_SALE,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  useExperiments,
  CUSTOMER_PROFILE_EXPERIMENT,
  addTrackingProperties,
  CUSTOMER_PROFILE_VARIANTS,
  CONTROL,
  GLOBAL_MOBILE_NAV_EXPERIMENT,
  STAYS_SEARCH,
  AVAILABILITY_CLUSTER_MAP,
  CLUSTER_MAP_VARIANTS,
} from "../../../../context/experiments";
import { RouteComponentProps } from "react-router-dom";
import {
  TRAVEL_SALES_EVENT_ACTIVE_CTA,
  TRAVEL_SALES_EVENT_ACTIVE_SUBTITLE,
} from "../../textConstants";
import { PATH_TRAVEL_SALE } from "../../../../utils/paths";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { config } from "../../../../api/config";
import { CLEAR_ALL_FILTERS, IIdLodgings } from "redmond";
import { getStayTypeFilterConfig } from "../utilities";
import { StayTypeFilterControl } from "../StayTypeFilterControl";
import { StayTypeFilterPopover } from "../StayTypeFilterPopover";
import { ConnectedAvailabilityClusterMap } from "../AvailabilityClusterMap/container";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";

export interface IMobileHotelAvailabilityProps
  extends MobileHotelAvailabilityConnectorProps,
    RouteComponentProps {
  onShowMap?: (open: boolean) => void;
}

export const MobileHotelAvailability = (
  props: IMobileHotelAvailabilityProps
) => {
  const {
    lodgings,
    lodgingIdInFocus,
    isFilteredHotelAvailabilityLodgingsEmpty,
    hotelAvailabilityCallState,
    history,
    shouldApplyUserHotelPreferences,
    resetFilters,
    appliedFilterCount,
    onShowMap,
    availabilityResultsHasHomesAndHotels,
    stayTypes,
    searchedLocation,
    isSearchingMap,
    isTravelApp,
  } = props;

  const expState = useExperiments();

  // Required state for showing the count heading text
  const [locationName, setLocationName] = React.useState("");
  const [isSearchTermLodging, setIsSearchTermLodging] = React.useState(false);
  // If search term is location (ex: Toronto) vs if search term is point of interest (ex: Toronto Zoo)
  const [isSearchTermPoint, setIsSearchTermPoint] = React.useState(false);

  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );
  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const travelSalesEventVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS
  );

  const isCustomerProfileExperiment =
    getExperimentVariantCustomVariants(
      expState.experiments,
      CUSTOMER_PROFILE_EXPERIMENT,
      CUSTOMER_PROFILE_VARIANTS
    ) !== CONTROL;

  const globalMobileNavExperimentVariant = getExperimentVariant(
    expState.experiments,
    GLOBAL_MOBILE_NAV_EXPERIMENT
  );
  const isGlobalMobileNavExperiment = React.useMemo(
    () => globalMobileNavExperimentVariant === AVAILABLE,
    [globalMobileNavExperimentVariant]
  );

  const mapVariantExp = getExperimentVariantCustomVariants(
    expState.experiments,
    AVAILABILITY_CLUSTER_MAP,
    CLUSTER_MAP_VARIANTS
  );

  const staysSearchEnabled =
    getExperimentVariant(expState.experiments, STAYS_SEARCH) === AVAILABLE;

  const initialCallInProgress =
    hotelAvailabilityCallState ===
    HotelAvailabilityCallState.InitialSearchCallInProcess;

  const {
    dedicatedStayTypeFilterButtonGroupEnabled,
    dedicatedStayTypeFilterEnabled,
  } = getStayTypeFilterConfig({
    experiments: expState.experiments,
    availabilityResultsHasHomesAndHotels,
    stayTypes,
  });

  const [showMap, setShowMap] = React.useState(false);

  const [filterModalOpen, setFilterModalOpen] = React.useState<boolean>(false);

  useEffect(() => {
    onShowMap?.(showMap);
    if (showMap) {
      // removes medallia from the fixed bottom that was covering pricing (only on the enlarged map)
      if (document && document.getElementById("nebula_div_btn")) {
        document!.getElementById("nebula_div_btn")!.style.display = "none";
      }
    }
    return () => {
      if (document && document.getElementById("nebula_div_btn")) {
        document!.getElementById("nebula_div_btn")!.style.display = "unset";
      }
    };
  }, [showMap]);

  useEffect(() => {
    const [locationName] = searchedLocation?.label
      ? searchedLocation.label.split(",")
      : [];
    setLocationName(locationName);

    const placeTypes = searchedLocation
      ? (searchedLocation.id as IIdLodgings).lodgingSelection.placeTypes
      : [];

    setIsSearchTermLodging(placeTypes?.includes("lodging"));
    setIsSearchTermPoint(
      !placeTypes?.includes("locality") &&
        !placeTypes?.includes("political") &&
        !placeTypes?.includes("country")
    );
  }, [searchedLocation]);

  const renderMap = (isPreview: boolean, onClickPreview?: () => void) => {
    return <AvailabilityMap isPreview={isPreview} onClick={onClickPreview} />;
  };

  const renderFullHeightMap = () => {
    return mapVariantExp === CONTROL ? (
      <Box className="mobile-lodging-availability-map-wrapper">
        <Box
          className={clsx("mobile-lodging-availability-map-container", {
            "full-height": lodgingIdInFocus === null,
          })}
        >
          {renderMap(false)}
          <Box className="map-back-button-container">
            <BackButton onClick={() => setShowMap(false)} />
          </Box>
          <Box className="map-filters-button-container">
            <AvailabilityFilter />
          </Box>
        </Box>
        <Box className="mobile-lodging-availability-map-bottom-items">
          <Box className="mobile-lodging-availability-map-rolling-gallery">
            <MobileAvailabilityMapRollingGallery />
          </Box>
        </Box>
      </Box>
    ) : (
      <Box
        position="relative"
        height="calc(100%)"
        top="-48px"
        width="auto"
        zIndex={2}
      >
        <Box
          position="absolute"
          top="20px"
          left="50%"
          width="100%"
          display="flex"
          justifyContent="space-between"
          zIndex={3}
          style={{
            transform: "translate(-50%, 0%)",
          }}
        >
          <Box
            width="34px"
            height="34px"
            justifyContent="center"
            display="flex"
            alignItems="center"
            bgcolor="white"
            marginLeft="10px"
            border="0.5px solid var(--grey-5)"
            borderRadius="4px"
          >
            <Link
              aria-label="back"
              component="button"
              onClick={() => setShowMap(false)}
            >
              <FontAwesomeIcon color="var(--blue-9)" icon={faChevronLeft} />
            </Link>
          </Box>
          <AvailabilityFilter />
        </Box>
        <ConnectedAvailabilityClusterMap />
      </Box>
    );
  };

  const scrollTrigger = useScrollTrigger({ disableHysteresis: true });

  const renderLocationSearch = () => {
    return (
      <Box
        className={clsx("mobile-location-search-contents", {
          "global-mobile-nav": isGlobalMobileNavExperiment,
          scrolled: scrollTrigger,
          "has-stay-type-filter": dedicatedStayTypeFilterButtonGroupEnabled,
        })}
      >
        <MobileLocationSelection
          fullScreen
          filterModalOpen={filterModalOpen}
          setFilterModalOpen={setFilterModalOpen}
        />
      </Box>
    );
  };

  const renderRewardsAccount = () => {
    return (
      <Box
        className={clsx("mobile-rewards-account-contents", {
          "global-mobile-nav": isGlobalMobileNavExperiment,
        })}
      >
        <RewardsAccountSelection className="b2b" popoverClassName="b2b" />
      </Box>
    );
  };

  const onClickPreview = () => {
    setShowMap(true);
    window.scrollTo(0, 0);
  };

  const renderHotelListView = () => (
    <Box className="mobile-lodging-availability-contents">
      {isCustomerProfileExperiment && shouldApplyUserHotelPreferences && (
        <UserPreferencesAppliedBanner
          resetFilters={() => {
            resetFilters();

            trackEvent({
              eventName: CLEAR_ALL_FILTERS,
              properties: addTrackingProperties(expState.trackingProperties),
            });
          }}
          type={"hotel"}
        />
      )}
      {travelSalesEventVariant === TRAVEL_SALE_ACTIVE && (
        <TravelSalesEventBanner
          variant="default"
          onClick={() => {
            const path = `${PATH_TRAVEL_SALE}?entryType=hotel_list_component`;
            history.push(path);
          }}
          subtitle={TRAVEL_SALES_EVENT_ACTIVE_SUBTITLE}
          buttonText={TRAVEL_SALES_EVENT_ACTIVE_CTA}
        />
      )}
      {dedicatedStayTypeFilterButtonGroupEnabled && (
        <>
          <Box
            className={clsx("mobile-availability-stay-type-filter-wrapper", {
              scrolled: scrollTrigger,
            })}
          >
            <StayTypeFilterControl />
          </Box>
          <Divider />
          {!!lodgings && lodgings.length > 0 && (
            <Box paddingY="20px" paddingX="20px">
              <Typography style={{ fontSize: "14px" }}>
                {COUNT_HEADING_TEXT(
                  lodgings.filter(
                    (curr) =>
                      typeof curr.available === "undefined" || curr.available
                  ).length,
                  locationName,
                  isSearchTermLodging,
                  isSearchTermPoint,
                  isSearchingMap,
                  staysSearchEnabled,
                  dedicatedStayTypeFilterEnabled,
                  stayTypes
                )}
              </Typography>
            </Box>
          )}
        </>
      )}
      {!!lodgings && lodgings.length > 0 && (
        <Box
          className={clsx(
            "mobile-availability-map-preview",
            "enhanced-hotel-filters"
          )}
          style={{
            ...(dedicatedStayTypeFilterButtonGroupEnabled && {
              marginTop: "0px",
            }),
          }}
        >
          {renderMap(true, onClickPreview)}
        </Box>
      )}
      {!isFilteredHotelAvailabilityLodgingsEmpty && !initialCallInProgress && (
        <Box className="mobile-availability-list-wrapper">
          <AvailabilityList />
        </Box>
      )}
      {isGlobalMobileNavExperiment && !initialCallInProgress && (
        <FloatingMenuPill
          items={[
            {
              label: "Map",
              onClick: () => setShowMap(true),
              icon: IconName.MapOutline,
            },
            {
              label: "Sort & filter",
              onClick: () => setFilterModalOpen(true),
              icon: IconName.Settings,
              badge: appliedFilterCount
                ? String(appliedFilterCount)
                : undefined,
            },
          ]}
        />
      )}
    </Box>
  );

  return (
    <>
      <Box
        className={clsx("mobile-lodging-availability-page", config.TENANT, {
          "travel-app": isTravelApp,
        })}
      >
        {!showMap && (
          <>
            {renderLocationSearch()}
            {renderRewardsAccount()}
            {!isFilteredHotelAvailabilityLodgingsEmpty && renderHotelListView()}

            {isGlobalMobileNavExperiment ? <BackToTopButton /> : undefined}

            {!isGlobalMobileNavExperiment && isTravelWalletCreditsExperiment ? (
              <TravelWalletDrawer />
            ) : undefined}
          </>
        )}
        {showMap && renderFullHeightMap()}
        {isFilteredHotelAvailabilityLodgingsEmpty && (
          <Box className="mobile-availability-no-results-wrapper">
            <AvailabilityNoResults />
          </Box>
        )}
        <StayTypeFilterPopover />
      </Box>
    </>
  );
};
